import { Dayjs } from "dayjs";
import { AmountType } from "./AmountType";
import { LineItemCode, LineItemType } from "./LineItem";
import { Media } from "./Media";
import { MerchandiseInventoryPolicy } from "./Merchandise";
import { UnitType } from "./Unit";
import { OversellPolicy } from "./WareInventory";

export interface CatalogItemPhoto {
    id: ID;
    src: string;
    type: "photo";
    caption: string | null;
    title: string;
}

/** The line item an add-on takes its quantity from when it tracks the guest count. */
export type QuantitySource = LineItemCode.PRIMARY_UNIT | LineItemCode.DEMOGRAPHIC;

/**
 * Template will not be there for choice addons, but will always be there for other addons, demographics
 */
export interface CatalogItem {
    id?: ID;
    name: string;
    template?: CatalogTemplateItem;
    defaultChoice?: string;
    items?: Array<AddOn>;
    type: CatalogType;
    caption: string;
    prices?: {
        price?: CatalogPrice;
        priceWithTaxes?: CatalogPrice;
    };
    parent?: { id: ID };
    unitType: UnitType;
    object: CatalogItemObject;
    overrideParent?: boolean;
    _remove?: boolean;
    photo?: CatalogItemPhoto;
    seller: { id: ID };
    subProduct?: { id: ID };
    sequence?: number;
    product?: { id: ID };
    displaySections: Array<CatalogItemDisplaySection>;
    required?: boolean;
    unit: { id: ID };
    quantitySource?: QuantitySource | null;
}

export enum CatalogItemDisplaySection {
    ADDONS = "addons",
    DEMOGRAPHICS = "demographics",
    QUESTIONNAIRE = "questionnaire",
}

export interface ProductCatalogItem extends CatalogItem {
    product?: { id: ID };
    parent?: { id: ID };
    overrideParent?: boolean;
    sequence?: number;
    displayName?: string;
    restrictions?: CatalogItemRestriction[];
}

export interface EquipmentCatalogItem extends CatalogItem {
    object: CatalogItemObject.EQUIPMENT_CATALOG_ITEM;
    unitType: UnitType.EQUIPMENT;
    template: CatalogTemplateItem;
    product?: { id: ID };
    sequence?: number;
    displayName?: string;
    capacity?: number;
    count?: number;
}

export enum CatalogItemObject {
    GLOBAL_CATALOG_ITEM = "global_catalog_item",
    PRODUCT_CATALOG_ITEM = "product_catalog_item",
    PICKUP_CATALOG_ITEM = "pickup_global_catalog_item",
    PICKUP_PRODUCT_CATALOG_ITEM = "pickup_product_catalog_item",
    WARE_CATALOG_ITEM = "ware_catalog_item",
    EQUIPMENT_CATALOG_ITEM = "equipment_catalog_item",
}

export enum DeleteCatalogItemConflictReason {
    CATALOG_ITEM_LINKED_TO_PRODUCTS = "catalog_item_linked_to_products",
    PRODUCT_HAS_NO_DEMOGRAPHIC_CATALOG_ITEM = "product_has_no_demographic_catalog_items",
}

export enum CatalogItemVisibility {
    PRIVATE = "private",
    PUBLIC = "public",
}

export enum CatalogItemRestrictionScope {
    PURCHASE_ITEM = "purchase_item",
    EVENT = "event",
    PRIMARY_UNIT = "primary_unit",
}

export interface CatalogItemRestriction {
    scope: CatalogItemRestrictionScope;
    value: number;
    allowOverride?: boolean;
}

export interface AddOn extends CatalogItem {
    id: ID;
    unitType: UnitType.ADDON | UnitType.PICKUP;
    visibility: CatalogItemVisibility;
    price: number;
    required: boolean;
    priceType: AmountType;
    defaultChoice?: string;
    sequence: number;
    deletedAt?: Dayjs;
    restrictions?: CatalogItemRestriction[];
}

export interface PickupCatalogItem extends Omit<CatalogItem, "prices" | "type"> {
    id: ID;
    type: UnitType.PICKUP;
    unitType: UnitType.PICKUP;
    visibility: CatalogItemVisibility;
    prices?: {
        perPerson?: {
            price: number;
            priceType: AmountType;
        };
        perGroup?: {
            price: number;
            priceType: AmountType;
        };
    };
    location: GooglePlaceLocation;
    arrivalOffset?: number | null;
    isDeclinePickupItem?: boolean;
    instructions?: string;
    defaultChoice?: string;
    required: boolean;
    sequence: number;
    deletedAt?: Dayjs;
}

export interface GooglePlaceLocation {
    postalAddress: {
        streetLines: string;
        city: string;
        region?: string;
        postalCode?: string;
        countryCode?: string;
        addressLines?: string[];
    };
    formattedAddress?: string;
    name?: string;
    externalIdentifiers?: {
        remoteId: string;
        remoteSystem: string;
    }[];
}

export interface GoogleLocationResponse {
    name: string;
    place_id: string;
    formatted_address?: string;
    location: {
        latitude: number;
        longitude: number;
    };

    postal_address: {
        locality: string;
        streetLines: string;
        city: string;
        administrativeArea?: string;
        regionCode?: string;
        region?: string;
        postalCode?: string;
        countryCode?: string;
        addressLines?: string[];
    };
    externalIdentifiers?: {
        remoteId: string;
        remoteSystem: string;
    }[];
}
export interface CatalogTemplateItem {
    code?: string;
    id: ID;
    type: LineItemType;
    name?: string;
    unit: { id: ID };
}

export enum CatalogType {
    CHOICES = "choices",
    CHOICES_ITEM = "choices_item",
    QUANTITY = "quantity",
    BOOLEAN = "boolean",
}

/**
 * CatalogPrice is returning {} sometimes
 */
export interface CatalogPrice {
    min?: number;
    max?: number;
    priceType?: CatalogPriceType;
}

export enum CatalogPriceType {
    PERCENT = "percent",
    PER_UNIT = "per_unit",
}

/**
 * Valid range for demographics from 0 to 10.000.000 but starting from 10.000 since we need it for reordering logic.
 * Valid range for addon > 10.000.000, 1st addon will have 10_010_000 sequence.
 * START_ADDON needed to recalculate starting sequence in case when item moved to the 1st position in the list.
 * Default step between items 10.000.
 */
export enum CatalogItemSequences {
    DEMOGRAPHICS_SEQUENCE_START = 0,
    EQUIPMENT_SEQUENCE_START = 5_000_000,
    ADDON_SEQUENCE_START = 10_000_000,
    STEP = 10_000,
}

export enum CatalogItemSequenceType {
    DEMOGRAPHICS = "demographics",
    EQUIPMENT = "equipment",
    ADDON = "merchandise",
}

export enum MerchandiseCatalogItemVisibility {
    NONE = "none",
    PRIVATE = "private",
    PUBLIC = "public",
}

export enum MerchandiseCatalogItemStatus {
    DRAFT = "draft",
    PUBLISHED = "published",
}

export interface MerchandiseCatalogItem extends Omit<CatalogItem, "prices"> {
    medias: Media[];
    strikeThroughPrice?: number;
    barcode?: string;
    prices?: {
        price?: CatalogPrice;
        perUnit?: {
            price: number;
            priceType: AmountType;
        };
    };
    inventoryPolicy: MerchandiseInventoryPolicy;
    oversellPolicy?: OversellPolicy;
    available?: number;
    visibility?: MerchandiseCatalogItemVisibility;
    sku: string;
    lowInventoryThreshold?: number | null;
    price?: number;
    status?: MerchandiseCatalogItemStatus;
}
